/* Darkstone App Hub Drawer — site-wide persistent panel */
(function () {
  'use strict';

  const OPEN_CLASS = 'is-open';
  const SELECTOR_TRIGGER = '[data-app-hub-trigger]';
  const SELECTOR_DRAWER = '[data-app-hub-drawer]';
  const SELECTOR_BACKDROP = '[data-app-hub-backdrop]';
  const SELECTOR_CLOSE = '[data-app-hub-close]';
  const BODY_LOCK_CLASS = 'dshub-body-locked';

  let drawer = null;
  let backdrop = null;
  let lastTrigger = null;
  const backgroundState = new Map();

  function resolveElements() {
    drawer = document.querySelector(SELECTOR_DRAWER);
    backdrop = document.querySelector(SELECTOR_BACKDROP);
  }

  function getTriggers() {
    return Array.from(document.querySelectorAll(SELECTOR_TRIGGER));
  }

  function syncTriggers(expanded) {
    getTriggers().forEach(function (trigger) {
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      if (drawer) {
        trigger.setAttribute('aria-controls', drawer.id || 'dshub-drawer');
      }
    });
  }

  function setBackgroundInert(shouldBeInert) {
    if (!shouldBeInert) {
      backgroundState.forEach(function (inert, element) {
        if (inert === null) element.removeAttribute('inert');
        else element.setAttribute('inert', inert);
      });
      backgroundState.clear();
      return;
    }

    // Shopify wraps sections: isolate sibling branches, never the dialog's
    // ancestors or the backdrop needed for pointer dismissal.
    function isolate(element) {
      if (element === drawer || element === backdrop) return;
      if (element.contains(drawer) || (backdrop && element.contains(backdrop))) {
        Array.from(element.children).forEach(isolate);
      } else if (!backgroundState.has(element)) {
        backgroundState.set(element, element.getAttribute('inert'));
        element.setAttribute('inert', '');
      }
    }
    Array.from(document.body.children).forEach(isolate);
  }

  function getFocusableElements() {
    return Array.from(drawer.querySelectorAll(
      'a[href], button, input, select, textarea, [tabindex]'
    )).filter(function (element) {
      const visibility = window.getComputedStyle(element).visibility;
      return element.tabIndex >= 0 && !element.matches(':disabled') &&
        !element.closest('[hidden], [inert]') && element.getClientRects().length > 0 &&
        visibility !== 'hidden' && visibility !== 'collapse';
    });
  }

  function openDrawer(trigger) {
    resolveElements();
    if (!drawer) return;

    lastTrigger = trigger || lastTrigger;
    drawer.removeAttribute('inert');
    if (!drawer.hasAttribute('tabindex')) drawer.setAttribute('tabindex', '-1');
    drawer.setAttribute('aria-hidden', 'false');
    drawer.classList.add(OPEN_CLASS);
    if (backdrop) {
      backdrop.setAttribute('aria-hidden', 'false');
      backdrop.classList.add(OPEN_CLASS);
    }
    document.body.classList.add(BODY_LOCK_CLASS);
    syncTriggers(true);
    setBackgroundInert(true);

    const firstFocusable = getFocusableElements()[0] || drawer;
    firstFocusable.focus({ preventScroll: true });
  }

  function closeDrawer(options) {
    const settings = options || {};
    resolveElements();

    if (drawer) {
      drawer.setAttribute('inert', '');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.classList.remove(OPEN_CLASS);
    }
    if (backdrop) {
      backdrop.setAttribute('aria-hidden', 'true');
      backdrop.classList.remove(OPEN_CLASS);
    }
    document.body.classList.remove(BODY_LOCK_CLASS);
    syncTriggers(false);
    setBackgroundInert(false);

    if (settings.restoreFocus !== false) {
      const focusTarget = lastTrigger && lastTrigger.isConnected ? lastTrigger : getTriggers()[0];
      if (focusTarget) {
        focusTarget.focus({ preventScroll: true });
      }
    }
    lastTrigger = null;
  }

  function handleClick(event) {
    const trigger = event.target.closest(SELECTOR_TRIGGER);
    if (trigger) {
      event.preventDefault();
      openDrawer(trigger);
      return;
    }

    if (event.target.closest(SELECTOR_CLOSE) || event.target.closest(SELECTOR_BACKDROP)) {
      event.preventDefault();
      closeDrawer();
    }
  }

  function init() {
    resolveElements();
    if (drawer) {
      drawer.setAttribute('inert', '');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.classList.remove(OPEN_CLASS);
    }
    if (backdrop) {
      backdrop.setAttribute('aria-hidden', 'true');
      backdrop.classList.remove(OPEN_CLASS);
    }
    document.body.classList.remove(BODY_LOCK_CLASS);
    syncTriggers(false);

    document.addEventListener('click', handleClick);
    document.addEventListener('darkstone:close-app-hub', function () {
      closeDrawer({ restoreFocus: false });
    });
    document.addEventListener('keydown', function (event) {
      resolveElements();
      if (!drawer || !drawer.classList.contains(OPEN_CLASS)) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDrawer();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = getFocusableElements();
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (!first) {
        event.preventDefault();
        drawer.focus({ preventScroll: true });
      } else if (event.shiftKey && (active === first || !focusable.includes(active))) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && (active === last || !focusable.includes(active))) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
